import React, { useEffect, useState } from 'react';
import { CheckCircle, Sparkles, Crown, ArrowRight, Loader2 } from 'lucide-react';
import { useNavigate } from 'react-router-dom';
import { useAuth } from '../App';
import { doc, getDoc } from 'firebase/firestore';
import { db } from '../App';

const PaymentSuccessPage = () => {
  const [countdown, setCountdown] = useState(5);
  const [userEmail, setUserEmail] = useState('');
  const [loading, setLoading] = useState(true);
  const [isPremium, setIsPremium] = useState(false);
  const { user: currentUser } = useAuth();
  const navigate = useNavigate();

  useEffect(() => {
    const verifyPaymentAndUser = async () => {
      console.log('🔍 Checking authentication state...');
      
      // Wait for authentication
      if (!currentUser) {
        console.log('⏳ Waiting for user authentication...');
        setTimeout(verifyPaymentAndUser, 1000);
        return;
      }

      console.log('✅ User authenticated:', currentUser.uid);
      setUserEmail(currentUser.email || 'your-email@example.com');

      // Get session ID from URL
      const params = new URLSearchParams(window.location.search);
      const sessionId = params.get('session_id');
      console.log('Session ID:', sessionId);

      // Check if user is premium in Firestore
      try {
        const userRef = doc(db, 'users', currentUser.uid);
        const userSnap = await getDoc(userRef);
        
        if (userSnap.exists()) {
          const userData = userSnap.data();
          console.log('📊 User data:', userData);
          
          if (userData.membershipType === 'premium' || userData.subscriptionStatus === 'active') {
            console.log('✅ User is PREMIUM!');
            setIsPremium(true);
            setLoading(false);
          } else {
            console.log('⏳ User not premium yet, waiting for webhook...');
            // Webhook might still be processing, check again in 2 seconds
            setTimeout(verifyPaymentAndUser, 2000);
          }
        } else {
          console.log('❌ User document not found');
          setLoading(false);
        }
      } catch (error) {
        console.error('❌ Error checking user status:', error);
        setLoading(false);
      }
    };

    verifyPaymentAndUser();
  }, [currentUser]);

  useEffect(() => {
    if (!isPremium) return;

    console.log('⏱️ Starting countdown...');
    // Start countdown only after premium is confirmed
    const timer = setInterval(() => {
      setCountdown((prev) => {
        if (prev <= 1) {
          clearInterval(timer);
          console.log('🚀 Redirecting to dashboard...');
          navigate('/dashboard');
          return 0;
        }
        return prev - 1;
      });
    }, 1000);

    return () => clearInterval(timer);
  }, [isPremium, navigate]);

  // Loading state while verifying payment
  if (loading) {
    return (
      <div
        className="min-h-screen flex items-center justify-center p-6"
        style={{
          background: 'linear-gradient(135deg, hsl(221, 45%, 18%) 0%, hsl(221, 35%, 25%) 100%)',
        }}
      >
        <div className="text-center">
          <Loader2 className="w-16 h-16 text-yellow-400 animate-spin mx-auto mb-4" />
          <h2 className="text-2xl font-bold text-white mb-2">
            Verifying your payment...
          </h2>
          <p className="text-gray-300">
            Please wait while we confirm your premium upgrade
          </p>
        </div>
      </div>
    );
  }

  return (
    <div
      className="min-h-screen flex items-center justify-center p-6"
      style={{
        background: 'linear-gradient(135deg, hsl(221, 45%, 18%) 0%, hsl(221, 35%, 25%) 100%)',
      }}
    >
      <div className="max-w-2xl w-full">
        {/* Success Card */}
        <div className="bg-white rounded-3xl shadow-2xl p-12 text-center relative overflow-hidden">
          {/* Background Decoration */}
          <div className="absolute top-0 left-0 w-full h-2"
            style={{ background: 'linear-gradient(90deg, hsl(43, 74%, 52%) 0%, hsl(43, 84%, 62%) 100%)' }}
          ></div>

          {/* Animated Success Icon */}
          <div className="mb-8 relative inline-block">
            <div className="relative">
              <div
                className="w-32 h-32 rounded-full flex items-center justify-center mx-auto animate-pulse"
                style={{
                  background: 'linear-gradient(135deg, hsl(43, 74%, 52%) 0%, hsl(43, 84%, 62%) 100%)',
                }}
              >
                <CheckCircle size={64} className="text-white" />
              </div>
              
              {/* Floating sparkles */}
              <div className="absolute -top-4 -right-4 animate-bounce">
                <Sparkles size={32} style={{ color: 'hsl(43, 74%, 52%)' }} />
              </div>
              <div className="absolute -bottom-2 -left-2 animate-bounce" style={{ animationDelay: '0.2s' }}>
                <Sparkles size={24} style={{ color: 'hsl(43, 74%, 52%)' }} />
              </div>
            </div>
          </div>

          {/* Success Message */}
          <h1 className="text-4xl font-bold mb-4" style={{ color: 'hsl(221, 45%, 18%)' }}>
            🎉 Payment Successful!
          </h1>
          
          <p className="text-xl text-gray-600 mb-6">
            Congratulations! You are now a Premium Member
          </p>

          {/* Premium Badge */}
          <div className="inline-flex items-center gap-2 px-6 py-3 rounded-full mb-8"
            style={{ background: 'linear-gradient(135deg, hsl(43, 74%, 52%) 0%, hsl(43, 84%, 62%) 100%)' }}
          >
            <Crown size={24} className="text-white" />
            <span className="text-white font-bold text-lg">PREMIUM MEMBER</span>
          </div>

          {/* Benefits List */}
          <div className="bg-gray-50 rounded-xl p-6 mb-8 text-left max-w-md mx-auto">
            <h3 className="text-lg font-bold mb-4 text-center" style={{ color: 'hsl(221, 45%, 18%)' }}>
              You now have access to:
            </h3>
            <div className="space-y-3">
              {[
                '✨ Unlimited profile views',
                '💝 Unlimited shortlist',
                '💌 Unlimited interests',
                '⭐ Priority visibility',
                '🔍 Advanced filters',
                '🎯 24/7 Premium support',
              ].map((benefit, index) => (
                <div
                  key={index}
                  className="flex items-center gap-3 p-3 bg-white rounded-lg transform transition-all hover:scale-105"
                >
                  <span className="text-gray-700 font-medium">{benefit}</span>
                </div>
              ))}
            </div>
          </div>

          {/* Redirect Message */}
          <div className="mb-6">
            <p className="text-gray-600 mb-2">
              Your account has been upgraded to Premium
            </p>
            <p className="text-sm text-gray-500">
              Redirecting to dashboard in <span className="font-bold text-xl" style={{ color: 'hsl(43, 74%, 52%)' }}>{countdown}</span> seconds...
            </p>
          </div>

          {/* Manual Redirect Button */}
          <button
            onClick={() => navigate('/dashboard')}
            className="px-8 py-4 rounded-full text-white font-bold text-lg transition-all transform hover:scale-105 inline-flex items-center gap-2"
            style={{
              background: 'linear-gradient(135deg, hsl(43, 74%, 52%) 0%, hsl(43, 84%, 62%) 100%)',
            }}
          >
            Go to Dashboard Now
            <ArrowRight size={20} />
          </button>
        </div>

        {/* Additional Info */}
        <div className="mt-6 text-center text-gray-300 text-sm">
          <p>Receipt has been sent to {userEmail}</p>
        </div>
      </div>
    </div>
  );
};

export default PaymentSuccessPage;